import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  editNews,
  uploadNewsImage,
  clearImageUrl,
  resetNewsState,
  getNewsList,
} from "../../actions/NewsActions";
import RichTextEditor from "../../components/RichTextEditor";
import DOMPurify from "dompurify";
import "../../styles/NewsCreate.css";

function NewsEdit() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, success, uploadedImageUrl } = useSelector(
    (state) => state.news,
  );
  const { newsList } = useSelector((state) => state.news);
  const editorRef = useRef();
  const hasRedirected = useRef(false);

  const [form, setForm] = useState({
    title: "",
    date: "",
    mainImage: "",
    paragraphs: "",
  });

  // Load existing article data from Redux store
  useEffect(() => {
    if (newsList.length === 0) {
      dispatch(getNewsList());
      return;
    }
    const article = newsList.find((n) => n.Slug === slug);
    if (!article) return;

    setForm((prev) => {
      if (prev.title !== "") return prev;
      const dateObj = new Date(article.Date);
      const isValidDate = !isNaN(dateObj);
      const formattedDate = isValidDate
        ? `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`
        : "";
      return {
        title: article.Title,
        date: formattedDate,
        mainImage: article.MainImage || "",
        paragraphs: article.Paragraphs || "",
      };
    });
  }, [slug, newsList, dispatch]);

  // Set uploaded image URL into form
  useEffect(() => {
    if (uploadedImageUrl) {
      setForm((prev) => ({ ...prev, mainImage: uploadedImageUrl }));
      dispatch(clearImageUrl());
    }
  }, [uploadedImageUrl, dispatch]);

  // Redirect on success
  useEffect(() => {
    if (success && !hasRedirected.current) {
      hasRedirected.current = true;
      dispatch(resetNewsState());
      navigate("/");
    }
  }, [success, dispatch, navigate]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) dispatch(uploadNewsImage(file, slug));
  };

  const formatDateForBackend = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allowedTags = [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "a",
      "img",
      "div",
      "span",
    ];
    const allowedAttr = [
      "href",
      "src",
      "alt",
      "title",
      "width",
      "height",
      "class",
      "style",
    ];
    const cleanHtml = DOMPurify.sanitize(form.paragraphs, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: allowedAttr,
    });
    const newsData = {
      slug,
      title: form.title,
      date: formatDateForBackend(form.date),
      paragraphs: cleanHtml,
    };
    dispatch(editNews(slug, newsData));
  };

  const months = [
    ["01", "January"],
    ["02", "February"],
    ["03", "March"],
    ["04", "April"],
    ["05", "May"],
    ["06", "June"],
    ["07", "July"],
    ["08", "August"],
    ["09", "September"],
    ["10", "October"],
    ["11", "November"],
    ["12", "December"],
  ];

  const updateDatePart = (partIndex, value) => {
    const parts = form.date ? form.date.split("-") : ["", "01", "01"];
    parts[partIndex] = value;
    setForm({ ...form, date: parts.join("-") });
  };

  const dateParts = form.date ? form.date.split("-") : ["", "", ""];

  return (
    <div className="news-create">
      <h1>
        Edit Article:{" "}
        <span style={{ color: "#555", fontWeight: 400 }}>{slug}</span>
      </h1>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label>Date</label>
          <div className="date-selects">
            <select
              value={dateParts[0]}
              onChange={(e) => updateDatePart(0, e.target.value)}
              required
            >
              <option value="">Year</option>
              {Array.from(
                { length: 10 },
                (_, i) => new Date().getFullYear() - i,
              ).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span className="date-separator">/</span>
            <select
              value={dateParts[1]}
              onChange={(e) => updateDatePart(1, e.target.value)}
              required
            >
              <option value="">Month</option>
              {months.map(([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
            <span className="date-separator">/</span>
            <select
              value={dateParts[2]}
              onChange={(e) => updateDatePart(2, e.target.value)}
              required
            >
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) =>
                String(i + 1).padStart(2, "0"),
              ).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Main Image (upload new to replace)</label>
          <input type="file" accept="image/*" onChange={handleImageUpload} />
          {isLoading && <div>Uploading...</div>}
          {form.mainImage && (
            <img
              src={`${form.mainImage}?t=${Date.now()}`}
              alt="preview"
              className="image-preview"
            />
          )}
        </div>

        <div className="form-group">
          <label>Article Content</label>
          <RichTextEditor
            ref={editorRef}
            value={form.paragraphs}
            onChange={(html) => setForm((prev) => ({ ...prev, paragraphs: html }))}
            slug={slug}
          />
        </div>

        <div className="button-group">
          <button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="clear-btn"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewsEdit;
